import React, { useCallback, useEffect, useState, useRef } from 'react'
import { PropTypes } from 'prop-types'

import { useFlag } from '../FeatureFlag/use-flag'

const AnalyticsContext = React.createContext({
  isReady: false,
  track: null,
  addEventProperty: null
})

/**
 * Provides an abstraction to analytics so that we can
 * fire events once analytics is loaded.
 *
 * @param {Object} analyticsProxy An object implementing
 *   the analytics interface.
 */
export const AnalyticsProvider = React.memo(({ children, analyticsProxy }) => {
  const hasFlag = useFlag('oo-v3-heapanalytics-provider')

  // Ref is used for the analytics functions so they don't
  // trigger in a useEffect multiple times
  const isReadyRef = useRef(false)
  const [isReady, setIsReady] = useState(false)
  const deferQueueRef = useRef([])

  isReadyRef.current = isReady

  /**
   * Pushes on the execution queue. If hasFlag is false
   * this becomes a noop to avoid memory leaks.
   */
  const pushToDeferQueue = useCallback(action => {
    if (hasFlag !== false) {
      deferQueueRef.current.push(action)
    }
  }, [hasFlag])

  const track = useCallback((...args) => {
    if (isReadyRef.current && analyticsProxy) {
      if (analyticsProxy.track) {
        analyticsProxy.track(...args)
      }
    } else {
      pushToDeferQueue(['track', ...args])
    }
  }, [analyticsProxy, pushToDeferQueue])

  const addEventProperties = useCallback((...args) => {
    if (isReadyRef.current && analyticsProxy) {
      if (analyticsProxy.addEventProperties) {
        analyticsProxy.addEventProperties(...args)
      }
    } else {
      pushToDeferQueue(
        ['addEventProperties', ...args]
      )
    }
  }, [analyticsProxy, pushToDeferQueue])

  useEffect(() => {
    // load heap once
    if (hasFlag && analyticsProxy) {
      analyticsProxy.load(() => {
        setIsReady(true)
      })
    } else if (hasFlag === false) {
      // make sure legacy metrics still flow
      analyticsProxy.load()
      // avoid a memory leak
      deferQueueRef.current = []
    }
  }, [hasFlag, analyticsProxy, setIsReady])

  useEffect(() => {
    if (isReady && analyticsProxy) {
      // drain the queue
      deferQueueRef.current.forEach(([fnName, ...args]) => {
        switch (fnName) {
          case 'track':
            if (analyticsProxy.track) {
              analyticsProxy.track(...args)
            }
            break
          case 'addEventProperties':
            if (analyticsProxy.addEventProperties) {
              analyticsProxy.addEventProperties(...args)
            }
            break
        }
      })

      deferQueueRef.current = []
    }
  }, [isReady, analyticsProxy])

  const context = {
    isReady,
    track,
    addEventProperties
  }
  return (
    <AnalyticsContext.Provider value={context}>
      {children}
    </AnalyticsContext.Provider>
  )
})

AnalyticsProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node
  ]).isRequired,
  analyticsProxy: PropTypes.object.isRequired
}

/**
 * Usage:
 *   { track } = useAnalytics()
 *   useEffect(() => {
 *     track('my key', {...properties})
 *   }, [track])
 */
export const useAnalytics = () => React.useContext(AnalyticsContext)
